/**
 * Responsive application shell around children with shared header, desktop sidebar, mobile dock,
 * activity chooser, and installation/notification prompt. Keeps the central content scrollable
 * within the viewport and reserves mobile safe-area space.
 *
 * @module apps/web/src/components/layout/Layout
 */

import { ReactNode } from 'react';
import { Header } from './Header';
import { MobileDock, Sidebar } from './Sidebar';
import { NewActivityFlow } from '@/components/activities/NewActivityFlow';
import { PWAInstallPrompt } from '@/components/pwa/PWAInstallPrompt';
import { DataRequestBanner } from '@/components/notifications/DataRequestBanner';

/** Routed application content inserted between the shared header/sidebar chrome. */
interface LayoutProps {
  /** Controls or content supplied by the owning view. */
  children: ReactNode;
}

/**
 * Responsive application shell around children with shared header, desktop sidebar, mobile dock,
 * activity chooser, and installation/notification prompt. Keeps the central content scrollable
 * within the viewport and reserves mobile safe-area space.
 */
export function Layout({ children }: LayoutProps) {
  return (
    <div className="app-shell flex h-dvh min-h-dvh flex-col overflow-hidden bg-background">
      <Header />
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <Sidebar />
        <main className="native-scroll min-h-0 flex-1 overflow-auto px-3 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] pt-3 sm:px-4 md:p-6">
          <DataRequestBanner />
          {children}
        </main>
      </div>
      <MobileDock />
      <NewActivityFlow />
      <PWAInstallPrompt />
    </div>
  );
}
